import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient } from '../../config/api';
import { API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Report,
  Warning,
  CheckCircle,
  Schedule,
  Visibility,
  Close,
  ErrorOutline,
  CheckCircleOutline,
  Block,
  InfoOutlined,
  AssignmentInd,
  RateReview,
  Gavel,
} from '@mui/icons-material';

// ─── Design tokens ────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ───────────────────────────────────────────────────────
interface ReportAdminListItem {
  id: number;
  reporterId: number;
  reportedId: number;
  categoryKey: string;
  categoryTitle: string;
  priorityKey: string;
  priorityTitle: string;
  statusKey: string;
  statusTitle: string;
  adminId?: number;
  createdTime: string;
}

interface ReportAdminDetail extends ReportAdminListItem {
  description?: string;
  cardId?: number;
  adminNote?: string;
  sanctionTypeKey?: string;
  sanctionTypeTitle?: string;
  reviewedAt?: string;
  resolvedAt?: string;
  updatedTime?: string;
}

interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

interface AttachmentItem {
  id: number;
  storageKey: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string;
}

// ─── Helpers ─────────────────────────────────────────────────────
const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('ko-KR', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const formatRelative = (dateString: string) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (d > 0)  return `${d}일 전`;
  if (h > 0)  return `${h}시간 전`;
  return '방금 전';
};

// ─── Status config ────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string; icon: React.ReactNode }> = {
  SUBMITTED:     { label: '접수됨',   color: '#E65100', bg: '#FFF3E0', dot: '#FF6D00', icon: <Warning sx={{ fontSize: 13 }} /> },
  UNDER_REVIEW:  { label: '검토중',   color: '#1565C0', bg: '#E3F2FD', dot: '#1976D2', icon: <RateReview sx={{ fontSize: 13 }} /> },
  ACTION_TAKEN:  { label: '조치됨',   color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047', icon: <CheckCircleOutline sx={{ fontSize: 13 }} /> },
  DISMISSED:     { label: '기각됨',   color: '#616161', bg: '#F5F5F5', dot: '#9E9E9E', icon: <Block sx={{ fontSize: 13 }} /> },
  CLOSED:        { label: '종결됨',   color: '#37474F', bg: '#ECEFF1', dot: '#607D8B', icon: <Close sx={{ fontSize: 13 }} /> },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  LOW:      { label: '낮음', color: '#757575' },
  MEDIUM:   { label: '보통', color: '#F57C00' },
  HIGH:     { label: '높음', color: '#C62828' },
  CRITICAL: { label: '긴급', color: '#AD1457' },
};

const SANCTION_TYPES = [
  { value: 'WARNING',    label: '경고' },
  { value: 'SUSPENSION', label: '일시정지' },
  { value: 'BAN',        label: '영구정지' },
];

// ─── Sub-components ───────────────────────────────────────────────
interface StatCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  accent: string;
  pulse?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, value, label, accent, pulse }) => (
  <Card elevation={0} sx={{
    border: '1px solid #F0E8F5',
    borderLeft: `4px solid ${accent}`,
    borderRadius: '14px',
    p: 2.5,
    display: 'flex', alignItems: 'center', gap: 2,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
  }}>
    <Box sx={{
      width: 48, height: 48, borderRadius: '12px',
      background: `${accent}18`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: accent, flexShrink: 0,
      position: 'relative',
    }}>
      {icon}
      {pulse && value > 0 && (
        <Box sx={{
          position: 'absolute', top: -4, right: -4,
          width: 12, height: 12, borderRadius: '50%',
          background: accent,
          animation: 'reportPulse 1.5s ease-in-out infinite',
        }} />
      )}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: accent, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  </Card>
);

// ─── Main component ───────────────────────────────────────────────
const AdminReportsPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [reports, setReports]               = useState<ReportAdminListItem[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportAdminDetail | null>(null);
  const [detailDialog, setDetailDialog]     = useState(false);
  const [actionDialog, setActionDialog]     = useState(false);
  const [actionType, setActionType]         = useState<'REVIEW' | 'ACTION' | 'DISMISS'>('REVIEW');
  const [actionNote, setActionNote]         = useState('');
  const [sanctionType, setSanctionType]     = useState('WARNING');
  const [suspensionDays, setSuspensionDays] = useState(7);
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [page, setPage]                     = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [totalElements, setTotalElements]   = useState(0);
  const [loading, setLoading]               = useState(false);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [actionLoading, setActionLoading]   = useState(false);
  const [attachments, setAttachments]       = useState<AttachmentItem[]>([]);
  const [snack, setSnack]                   = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  const adminId = Number((user as any)?.userId ?? user?.id) || 0;

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), size: '20' });
      if (filterStatus)   qs.append('status', filterStatus);
      if (filterPriority) qs.append('priority', filterPriority);
      const res = await apiClient.get<{ resultType: string; result: PageResponse<ReportAdminListItem> }>(
        `${API_ENDPOINTS.ADMIN.REPORTS.LIST}?${qs.toString()}`
      );
      setReports(res.result.content);
      setTotalPages(res.result.totalPages);
      setTotalElements(res.result.totalElements);
    } catch (e) {
      console.error(e);
      showSnack('목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterPriority]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') fetchReports();
  }, [fetchReports, isLoading, user]);

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') => {
    setSnack({ open: true, msg, severity });
  };

  const openDetail = async (report: ReportAdminListItem) => {
    setDetailLoading(true);
    setDetailDialog(true);
    setAttachments([]);
    try {
      const res = await apiClient.get<{ resultType: string; result: ReportAdminDetail }>(
        API_ENDPOINTS.ADMIN.REPORTS.DETAIL(report.id)
      );
      setSelectedReport(res.result);
      try {
        const attRes = await apiClient.get<{ resultType: string; result: { attachments: AttachmentItem[] } }>(
          `/api/v1/gth-common/silver-connect/reports/${report.id}/attachments`
        );
        setAttachments(attRes.result.attachments ?? []);
      } catch {
        // attachment fetch failure is non-critical
      }
    } catch (e) {
      showSnack('상세 조회에 실패했습니다.', 'error');
      setDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAction = (report: ReportAdminDetail | ReportAdminListItem, type: 'REVIEW' | 'ACTION' | 'DISMISS') => {
    setSelectedReport(report as ReportAdminDetail);
    setActionType(type);
    setActionNote('');
    setSanctionType('WARNING');
    setSuspensionDays(7);
    setDetailDialog(false);
    setActionDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedReport) return;
    setActionLoading(true);
    try {
      if (actionType === 'REVIEW') {
        await apiClient.post(API_ENDPOINTS.ADMIN.REPORTS.REVIEW(selectedReport.id), { adminId });
        showSnack('검토 상태로 변경되었습니다.');
      } else if (actionType === 'ACTION') {
        await apiClient.post(API_ENDPOINTS.ADMIN.REPORTS.ACTION(selectedReport.id), {
          adminId, sanctionType, adminNote: actionNote || undefined,
          ...(sanctionType === 'SUSPENSION' && { suspensionDays }),
        });
        showSnack('조치가 완료되었습니다.');
      } else {
        await apiClient.post(API_ENDPOINTS.ADMIN.REPORTS.DISMISS(selectedReport.id), {
          adminId, adminNote: actionNote || undefined,
        });
        showSnack('신고가 기각되었습니다.');
      }
      setActionDialog(false);
      fetchReports();
    } catch (e) {
      showSnack('처리에 실패했습니다.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async (report: ReportAdminListItem) => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.REPORTS.ASSIGN(report.id), { adminId });
      showSnack('배정되었습니다.');
      fetchReports();
    } catch (e) {
      showSnack('배정에 실패했습니다.', 'error');
    }
  };

  const stats = {
    total:        totalElements,
    submitted:    reports.filter(r => r.statusKey === 'SUBMITTED').length,
    underReview:  reports.filter(r => r.statusKey === 'UNDER_REVIEW').length,
    actionTaken:  reports.filter(r => r.statusKey === 'ACTION_TAKEN').length,
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  return (
    <AdminLayout>
      <style>{`
        @keyframes reportPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(1.3); }
        }
        @keyframes rowEntrance {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <Box sx={{ p: { xs: 2, md: 4 }, background: '#F8F4F9', minHeight: '100vh' }}>

        {/* ── Page header ── */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="overline" sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>
              ADMIN · SAFETY
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" sx={{
                fontWeight: 900, color: '#1A0A2E', mt: 0.5,
                background: `linear-gradient(135deg, #1A0A2E 30%, ${CORAL})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                신고 관리
              </Typography>
              {stats.submitted > 0 && (
                <Chip
                  label={`대기 ${stats.submitted}건`}
                  size="small"
                  icon={<ErrorOutline sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    background: '#FF6D00', color: '#fff', fontWeight: 700,
                    fontSize: '0.7rem',
                    animation: 'reportPulse 2s ease-in-out infinite',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}
            </Box>
            <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.5 }}>
              커뮤니티 신고 접수 현황 및 처리
            </Typography>
          </Box>
        </Box>

        {/* ── Stat cards ── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 2, mb: 4,
        }}>
          <StatCard icon={<Report sx={{ fontSize: 24 }} />}        value={stats.total}       label="전체 신고"  accent="#7B1FA2" />
          <StatCard icon={<Warning sx={{ fontSize: 24 }} />}        value={stats.submitted}   label="접수 대기"  accent="#E65100" pulse />
          <StatCard icon={<Schedule sx={{ fontSize: 24 }} />}       value={stats.underReview} label="검토중"     accent="#1565C0" />
          <StatCard icon={<CheckCircle sx={{ fontSize: 24 }} />}    value={stats.actionTaken} label="조치 완료"  accent="#2E7D32" />
        </Box>

        {/* ── Filter bar ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>상태 필터</InputLabel>
            <Select
              value={filterStatus}
              label="상태 필터"
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="SUBMITTED">접수됨</MenuItem>
              <MenuItem value="UNDER_REVIEW">검토중</MenuItem>
              <MenuItem value="ACTION_TAKEN">조치됨</MenuItem>
              <MenuItem value="DISMISSED">기각됨</MenuItem>
              <MenuItem value="CLOSED">종결됨</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>우선순위</InputLabel>
            <Select
              value={filterPriority}
              label="우선순위"
              onChange={(e) => { setFilterPriority(e.target.value); setPage(0); }}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="LOW">낮음</MenuItem>
              <MenuItem value="MEDIUM">보통</MenuItem>
              <MenuItem value="HIGH">높음</MenuItem>
              <MenuItem value="CRITICAL">긴급</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* ── Table ── */}
        <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F0E8F5', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: PINK }} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                    {['ID', '카테고리', '우선순위', '신고 시각', '상태', '담당자', '처리'].map((h) => (
                      <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.75rem', py: 1.8, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report, idx) => {
                    const statusCfg  = STATUS_CONFIG[report.statusKey]   ?? STATUS_CONFIG['SUBMITTED'];
                    const priorityCfg = PRIORITY_CONFIG[report.priorityKey] ?? { label: report.priorityTitle, color: '#555' };
                    const isPending  = report.statusKey === 'SUBMITTED';
                    return (
                      <TableRow
                        key={report.id}
                        onClick={() => openDetail(report)}
                        sx={{
                          animation: `rowEntrance 0.3s ease ${idx * 0.04}s both`,
                          borderLeft: isPending ? '4px solid #FF6D00' : '4px solid transparent',
                          background: isPending ? '#FFF8F0' : '#fff',
                          '&:hover': { background: isPending ? '#FFF0E0' : '#FAF5FF' },
                          transition: 'background 0.2s ease',
                          '& td': { borderBottom: '1px solid #F3EBF8' },
                          cursor: 'pointer',
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>#{report.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={report.categoryTitle} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', background: '#F3E5F5', color: '#7B1FA2' }} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: priorityCfg.color }}>
                            {priorityCfg.label}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap' }}>{formatRelative(report.createdTime)}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#BBB' }}>{formatDateTime(report.createdTime)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                            background: statusCfg.bg,
                            color: statusCfg.color,
                            border: `1px solid ${statusCfg.color}33`,
                            px: 1.2, py: 0.4, borderRadius: '100px',
                            fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
                          }}>
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%',
                              background: statusCfg.dot,
                              ...(isPending && { animation: 'reportPulse 1.5s ease-in-out infinite' }),
                            }} />
                            {statusCfg.label}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: report.adminId ? '#333' : '#BBB' }}>
                            {report.adminId ? `#${report.adminId}` : '미배정'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                            <Tooltip title="상세 보기">
                              <IconButton size="small" onClick={() => openDetail(report)} sx={{ color: '#9C27B0', '&:hover': { background: '#F3E5F5' } }}>
                                <Visibility sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {!report.adminId && (
                              <Tooltip title="배정">
                                <IconButton size="small" onClick={() => handleAssign(report)} sx={{ color: '#1976D2', '&:hover': { background: '#E3F2FD' } }}>
                                  <AssignmentInd sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {report.statusKey === 'SUBMITTED' && (
                              <Tooltip title="검토 시작">
                                <IconButton size="small" onClick={() => openAction(report, 'REVIEW')} sx={{ color: '#1565C0', '&:hover': { background: '#E3F2FD' } }}>
                                  <RateReview sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {report.statusKey === 'UNDER_REVIEW' && (
                              <>
                                <Tooltip title="조치">
                                  <IconButton size="small" onClick={() => openAction(report, 'ACTION')} sx={{ color: '#2E7D32', '&:hover': { background: '#E8F5E9' } }}>
                                    <Gavel sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="기각">
                                  <IconButton size="small" onClick={() => openAction(report, 'DISMISS')} sx={{ color: '#E65100', '&:hover': { background: '#FFF3E0' } }}>
                                    <Block sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {reports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <InfoOutlined sx={{ fontSize: 40, color: '#DDD', mb: 1 }} />
                        <Typography sx={{ color: '#AAA' }}>신고 내역이 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, v) => setPage(v - 1)}
              color="primary"
              sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px' } }}
            />
          </Box>
        )}

        {/* ── Detail Dialog ── */}
        <Dialog
          open={detailDialog}
          onClose={() => setDetailDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: PINK }} />
            </Box>
          ) : selectedReport && (() => {
            const cfg = STATUS_CONFIG[selectedReport.statusKey] ?? STATUS_CONFIG['SUBMITTED'];
            return (
              <>
                <DialogTitle sx={{ p: 0 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1A0A2E 0%, #4A1265 100%)', p: 3, color: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: `${cfg.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <Report />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>신고 #{selectedReport.id} 상세</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{formatDateTime(selectedReport.createdTime)} 접수</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      {[
                        { label: '신고자 ID', value: `#${selectedReport.reporterId}`, color: '#1976D2' },
                        { label: '피신고자 ID', value: `#${selectedReport.reportedId}`, color: '#E65100' },
                      ].map((item) => (
                        <Box key={item.label} sx={{ p: 2, borderRadius: '12px', background: `${item.color}08`, border: `1px solid ${item.color}20` }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>{item.label}</Typography>
                          <Typography sx={{ fontWeight: 700, color: item.color }}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>카테고리</Typography>
                        <Chip label={selectedReport.categoryTitle} size="small" sx={{ background: '#F3E5F5', color: '#7B1FA2', fontWeight: 700 }} />
                      </Box>
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>우선순위</Typography>
                        <Typography sx={{ fontWeight: 700, color: PRIORITY_CONFIG[selectedReport.priorityKey]?.color ?? '#555' }}>
                          {selectedReport.priorityTitle}
                        </Typography>
                      </Box>
                    </Box>
                    {selectedReport.description && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>상세 내용</Typography>
                        <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>{selectedReport.description}</Typography>
                      </Box>
                    )}
                    {selectedReport.adminNote && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#FFF8E1', border: '1px solid #FFD54F' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>관리자 메모</Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{selectedReport.adminNote}</Typography>
                      </Box>
                    )}
                    {attachments.length > 0 && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>
                          첨부파일 ({attachments.length}개)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {attachments.map((att) => (
                            <Box
                              key={att.id}
                              component="a"
                              href={att.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                width: 80, height: 80, borderRadius: '8px', overflow: 'hidden',
                                border: '1px solid #E0E0E0', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                textDecoration: 'none',
                                '&:hover': { borderColor: '#9C27B0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                              }}
                            >
                              {att.mimeType.startsWith('image/') ? (
                                <img src={att.downloadUrl} alt="첨부파일" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Box sx={{ textAlign: 'center', p: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: '#666', wordBreak: 'break-all' }}>
                                    {att.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.55rem', color: '#999' }}>
                                    {(att.fileSize / 1024).toFixed(0)}KB
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ p: 2, borderRadius: '12px', background: `${cfg.color}08`, border: `1px solid ${cfg.color}25`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600 }}>처리 상태</Typography>
                        <Typography sx={{ fontWeight: 700, color: cfg.color, fontSize: '0.9rem' }}>{cfg.label}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                  {selectedReport.statusKey === 'SUBMITTED' && (
                    <Button variant="outlined" onClick={() => openAction(selectedReport, 'REVIEW')}
                      sx={{ borderRadius: '10px', borderColor: '#1565C0', color: '#1565C0', textTransform: 'none', fontWeight: 700 }}
                      startIcon={<RateReview sx={{ fontSize: 16 }} />}>
                      검토 시작
                    </Button>
                  )}
                  {selectedReport.statusKey === 'UNDER_REVIEW' && (
                    <>
                      <Button variant="outlined" onClick={() => openAction(selectedReport, 'DISMISS')}
                        sx={{ borderRadius: '10px', borderColor: '#E65100', color: '#E65100', textTransform: 'none', fontWeight: 700 }}
                        startIcon={<Block sx={{ fontSize: 16 }} />}>
                        기각
                      </Button>
                      <Button variant="contained" onClick={() => openAction(selectedReport, 'ACTION')}
                        sx={{ borderRadius: '10px', background: '#2E7D32', textTransform: 'none', fontWeight: 700, '&:hover': { background: '#1B5E20' } }}
                        startIcon={<Gavel sx={{ fontSize: 16 }} />}>
                        조치
                      </Button>
                    </>
                  )}
                  <Button onClick={() => setDetailDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>닫기</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* ── Action Dialog ── */}
        <Dialog
          open={actionDialog}
          onClose={() => setActionDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
          {selectedReport && (
            <>
              <DialogTitle sx={{ p: 0 }}>
                <Box sx={{
                  background: actionType === 'REVIEW' ? 'linear-gradient(135deg, #0D47A1, #1565C0)' :
                              actionType === 'ACTION' ? 'linear-gradient(135deg, #1B5E20, #2E7D32)' :
                              'linear-gradient(135deg, #BF360C, #E65100)',
                  p: 3, color: '#fff',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {actionType === 'REVIEW' ? <RateReview /> : actionType === 'ACTION' ? <Gavel /> : <Block />}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                        {actionType === 'REVIEW' ? '검토 시작' : actionType === 'ACTION' ? '조치 처리' : '신고 기각'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>
                        신고 #{selectedReport.id}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 3, pt: 3 }}>
                {actionType === 'ACTION' && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                      <InputLabel>제재 유형</InputLabel>
                      <Select value={sanctionType} label="제재 유형" onChange={(e) => setSanctionType(e.target.value)}
                        sx={{ borderRadius: '12px' }}>
                        {SANCTION_TYPES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {sanctionType === 'SUSPENSION' && (
                      <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <InputLabel>정지 기간</InputLabel>
                        <Select value={suspensionDays} label="정지 기간"
                          onChange={(e) => setSuspensionDays(Number(e.target.value))}
                          sx={{ borderRadius: '12px' }}>
                          <MenuItem value={7}>7일</MenuItem>
                          <MenuItem value={14}>14일</MenuItem>
                          <MenuItem value={30}>30일</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
                <TextField
                  fullWidth multiline rows={3}
                  label={actionType === 'REVIEW' ? '처리 메모 (선택)' : '사유 (선택)'}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  placeholder="내용을 입력해주세요..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button onClick={() => setActionDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>취소</Button>
                <Button
                  variant="contained"
                  disabled={actionLoading}
                  onClick={handleConfirmAction}
                  sx={{
                    borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3,
                    background: actionType === 'REVIEW' ? '#1565C0' : actionType === 'ACTION' ? '#2E7D32' : '#E65100',
                    '&:hover': { background: actionType === 'REVIEW' ? '#0D47A1' : actionType === 'ACTION' ? '#1B5E20' : '#BF360C' },
                  }}
                  startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> :
                             actionType === 'REVIEW' ? <RateReview /> : actionType === 'ACTION' ? <Gavel /> : <Block />}
                >
                  {actionType === 'REVIEW' ? '검토 시작' : actionType === 'ACTION' ? '조치 확인' : '기각 확인'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── Snackbar ── */}
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default AdminReportsPage;
